import React, { useState, useEffect } from "react";
import { Contract, ethers } from "ethers";
import {
  Button,
  Form,
  Container,
  Row,
  Col,
} from "react-bootstrap";
import {
  useWeb3ModalAccount,
  useWeb3ModalProvider,
} from "@web3modal/ethers/react";
import { useTranslation } from "react-i18next";
import {
  isTestEnv,
  presaleAbi,
  presaleAddress,
} from "../../utils/contants";
import { toast } from "react-toastify";
import { Affiliate } from "../../types/Affiliate.ts";
import {
  generateRandomCode,
} from "../../utils/utils.ts";
import { setAffiliate } from "../../store/firestore.ts";
import AffilaiteTable from "../../components/AffiliateTable.tsx";

const AdminAffiliatePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { walletProvider } = useWeb3ModalProvider();
  const { address } = useWeb3ModalAccount();


  // Contract and affiliate state
  const [presaleContract, setPresaleContract] =
    useState<ethers.Contract | null>(null);

  // Registration state
  const [userAddress, setUserAddress] = useState("");
  const [profitPercent, setProfitPercent] = useState("10");
  const [note, setNote] = useState("");
  const [registerLoading, setRegisterLoading] = useState(false);
  const [contractOwner, setContractOwner] = useState("");

  // Pagination & search

  const init = async () => {
    try {
      console.log("init called");
      if (!walletProvider) return;
      console.log("init worked");
      const provider = new ethers.BrowserProvider(walletProvider);
      const signer = await provider.getSigner();
      const contract = new Contract(presaleAddress, presaleAbi, signer);
      const ownerAddress = await contract.owner();
      setContractOwner(ownerAddress);
      setPresaleContract(contract);
    } catch (error) {
      console.error(error);
      toast.info(t("info.error"));
    }
  };

  const showTruncatedError = (error: unknown) => {
    const errorMessage = error?.toString() || "Unknown error";
    const truncatedMessage =
      errorMessage.length > 100
        ? `${errorMessage.slice(0, 50)}...${errorMessage.slice(-50)}`
        : errorMessage;
    toast.error(truncatedMessage);
  };


  const generateCode = async () => {
    if (!presaleContract) return "";
    while (true) {
      let code = generateRandomCode();
      let affiliate = Affiliate.fromEthersResponse(
        await presaleContract.affiliateMap(code)
      );
      if (affiliate.isNonExistant()) {
        return code;
      }
    }
  };

  const registerAffiliate = async () => {
    try {
      if (registerLoading) return;
      setRegisterLoading(true);
      if (!presaleContract) {
        throw new Error("Please connect your wallet");
      }
      if (!ethers.isAddress(userAddress)) {
        throw new Error("Invalid address");
      }
      const newCode = await generateCode();
      const tx = await presaleContract.registerAffiliate(
        newCode,
        userAddress,
        profitPercent
      );
      await tx.wait();
      await setAffiliate(userAddress, note);
      toast.success("Affiliate Registered");
      setUserAddress("");
      setProfitPercent("0");
    } catch (error) {
      console.error("Error registering affiliate:", error);
      showTruncatedError(error);
    } finally {
      setRegisterLoading(false);
    }
  };


  useEffect(() => {
    init();
  }, [walletProvider]);

  // // Only display details if connected address equals contract owner
  if (!isTestEnv && (!address || address.toLowerCase() !== contractOwner.toLowerCase())) {
    return (
      <Container className="text-center mt-5">
        <h3>{t('info.only_owner')}</h3>
      </Container>
    );
  }

 
  return (
    <Container className="mt-4">
      <h2 className="text-center mb-4">{t("affiliates.affiliates")}</h2>
      {/* Registration Form */}
      <Form className="mb-3">
        <Row className="align-items-end">
          <Col md={4}>
            <Form.Group controlId="userAddress">
              <Form.Label>
                <b>{t("affiliates.user_address")}</b>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={t("affiliates.enter_user_address")}
                value={userAddress}
                onChange={(e) => setUserAddress(e.target.value)}
              />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group controlId="note">
              <Form.Label>
                <b>{t("affiliates.note")} </b>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={t("affiliates.note")}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group controlId="profitPercent">
              <Form.Label>
                <b>{t("affiliates.profit")} %</b>
              </Form.Label>
              <Form.Control
                type="number"
                placeholder={t("affiliates.profit")}
                value={profitPercent}
                onChange={(e) => setProfitPercent(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Button onClick={registerAffiliate} variant="primary">
              {registerLoading
                ? t("affiliates.registering")
                : t("affiliates.register_affiliate")}
            </Button>
          </Col>
        </Row>
      </Form>
      <br />
      <hr />
      <br />
      <AffilaiteTable revokedOnly={false} />
    </Container>
  );
};

export default AdminAffiliatePage;
